import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, CAP_LABELS } from "@/lib/api";

export default function OverviewTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/coach/analytics").then((r) => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <p className="text-zinc-500 text-xs uppercase tracking-[0.25em] animate-pulse">Loading…</p>;

  const cards = [
    ["Total assessments", stats.total_assessments],
    ["Average score", stats.avg_score],
    ["This week", stats.assessments_week],
    ["This month", stats.assessments_month],
    ["Registrations", stats.total_registrations],
    ["Conversion", `${stats.conversion_rate}%`],
    ["Active members", stats.members],
    ["Marketing opt-ins", stats.marketing_optins],
  ];

  return (
    <div data-testid="coach-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
        {cards.map(([label, v]) => (
          <div key={label} className="bg-zinc-950 p-6">
            <p className="font-display text-5xl leading-none text-volt">{v}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="border border-zinc-800 bg-zinc-950 p-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">Most common development areas</p>
          {Object.entries(stats.development_areas).sort((a, b) => b[1] - a[1]).map(([cap, n]) => (
            <div key={cap} className="flex items-center gap-3 mb-2">
              <span className="text-xs uppercase tracking-[0.12em] text-zinc-300 w-28">{CAP_LABELS[cap]}</span>
              <div className="flex-1 h-1.5 bg-zinc-800"><div className="h-full bg-volt" style={{ width: `${(n / Math.max(1, stats.total_assessments)) * 100}%` }} /></div>
              <span className="font-display text-lg text-volt w-6 text-right">{n}</span>
            </div>
          ))}
          {!Object.keys(stats.development_areas).length && <p className="text-zinc-600 text-sm">No assessment data yet.</p>}
        </div>
        <div className="border border-zinc-800 bg-zinc-950 p-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">Age distribution</p>
          {["18-29", "30-44", "45-59", "60+"].map((a) => (
            <div key={a} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-zinc-300 w-14">{a}</span>
              <div className="flex-1 h-1.5 bg-zinc-800"><div className="h-full bg-zinc-400" style={{ width: `${((stats.age_distribution[a] || 0) / Math.max(1, stats.total_assessments)) * 100}%` }} /></div>
              <span className="font-display text-lg text-zinc-300 w-6 text-right">{stats.age_distribution[a] || 0}</span>
            </div>
          ))}
          <div className="mt-8 pt-4 border-t border-zinc-800 flex flex-wrap gap-3">
            <a data-testid="export-leads" href={`${process.env.REACT_APP_BACKEND_URL}/api/coach/export/leads.csv`}
              className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
              <Download size={14} /> Assessment leads CSV
            </a>
            <a data-testid="export-registrations" href={`${process.env.REACT_APP_BACKEND_URL}/api/coach/export/registrations.csv`}
              className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
              <Download size={14} /> Registrations CSV
            </a>
            <button data-testid="sheets-backfill" onClick={async () => {
                try {
                  const { data } = await api.post("/coach/sheets/backfill");
                  data.active ? toast.success(data.message) : toast.info(data.message);
                } catch (e) { toast.error("Sheets sync failed"); }
              }}
              className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-3 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
              <Download size={14} /> Sync all to Google Sheets
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
